/**
 * Template existence check with global fallback
 * 
 * Checks for active inspection template in order:
 * 1. Shop-specific template (shop_id = shopId, is_active = true)
 * 2. Global template (shop_id IS NULL, is_global = true, is_active = true)
 */

import { supabase } from '@/integrations/supabase/client';

export interface ActiveTemplateResult {
  templateId: string;
  isGlobal: boolean;
  templateKey: string;
  version: number;
}

/**
 * Check for an active inspection template, with global fallback
 * @param shopId - The shop ID to check for shop-specific template
 * @param templateKey - The template key (e.g., 'DVI', 'INTAKE')
 * @returns Template info if found, null otherwise
 */
export async function checkActiveTemplate(
  shopId: string,
  templateKey: string
): Promise<ActiveTemplateResult | null> {
  // First, check for shop-specific template
  const { data: shopTemplate, error: shopError } = await supabase
    .from('inspection_templates')
    .select('id, template_key, version, is_global')
    .eq('shop_id', shopId)
    .eq('template_key', templateKey)
    .eq('is_active', true)
    .maybeSingle();

  if (shopError) {
    console.error('Error checking shop-specific template:', shopError);
    return null;
  }

  if (shopTemplate) {
    return {
      templateId: shopTemplate.id,
      isGlobal: false,
      templateKey: shopTemplate.template_key,
      version: shopTemplate.version,
    };
  }

  // Fallback: check for global template (shop_id IS NULL, is_global = true)
  const { data: globalTemplate, error: globalError } = await supabase
    .from('inspection_templates')
    .select('id, template_key, version, is_global')
    .is('shop_id', null)
    .eq('is_global', true)
    .eq('template_key', templateKey)
    .eq('is_active', true)
    .maybeSingle();

  if (globalError) {
    console.error('Error checking global template:', globalError);
    return null;
  }

  if (globalTemplate) {
    return {
      templateId: globalTemplate.id,
      isGlobal: true,
      templateKey: globalTemplate.template_key,
      version: globalTemplate.version,
    };
  }

  return null;
}

/**
 * Quick check if any active template exists for the given key
 */
export async function hasActiveTemplate(
  shopId: string,
  templateKey: string
): Promise<boolean> {
  const result = await checkActiveTemplate(shopId, templateKey);
  return result !== null;
}
